/**
 * Dictée du navigateur (Web Speech API). Le moteur est celui du navigateur :
 * l'audio part en général chez Google ou Apple, d'où l'avis des réglages. Le
 * serveur ne reçoit que du texte, que la personne relit avant de l'envoyer.
 */
import { useCallback, useEffect, useRef, useState } from 'react';

interface ResultatReconnaissance {
  readonly isFinal: boolean;
  readonly 0: { readonly transcript: string };
}
interface EvenementReconnaissance {
  readonly resultIndex: number;
  readonly results: { readonly length: number; readonly [i: number]: ResultatReconnaissance };
}
interface Reconnaissance {
  lang: string;
  continuous: boolean;
  interimResults: boolean;
  onresult: ((e: EvenementReconnaissance) => void) | null;
  onerror: ((e: { readonly error: string }) => void) | null;
  onend: (() => void) | null;
  start: () => void;
  stop: () => void;
  abort: () => void;
}
type FabriqueReconnaissance = new () => Reconnaissance;

function fabrique(): FabriqueReconnaissance | null {
  if (typeof window === 'undefined') return null;
  const w = window as unknown as {
    SpeechRecognition?: FabriqueReconnaissance;
    webkitSpeechRecognition?: FabriqueReconnaissance;
  };
  return w.SpeechRecognition ?? w.webkitSpeechRecognition ?? null;
}

export type ErreurDictee = 'refusee' | 'indisponible';

export function useDictee({
  autorisee,
  langue,
  onTexte,
}: {
  /** Le réglage `dicteeWeb` : sans lui, rien n'est même instancié. */
  autorisee: boolean;
  langue: 'fr' | 'en';
  onTexte: (texte: string) => void;
}) {
  const [ecoute, setEcoute] = useState(false);
  const [erreur, setErreur] = useState<ErreurDictee | null>(null);
  const courante = useRef<Reconnaissance | null>(null);
  const rappel = useRef(onTexte);
  useEffect(() => {
    rappel.current = onTexte;
  }, [onTexte]);

  const disponible = autorisee && fabrique() !== null;

  const arreter = useCallback(() => {
    courante.current?.abort();
    courante.current = null;
    setEcoute(false);
  }, []);

  const demarrer = useCallback(() => {
    const Reconnaissance = autorisee ? fabrique() : null;
    if (!Reconnaissance) return;
    courante.current?.abort();
    setErreur(null);
    const r = new Reconnaissance();
    r.lang = langue === 'en' ? 'en-US' : 'fr-FR';
    r.continuous = false;
    r.interimResults = false;
    r.onresult = (e) => {
      let texte = '';
      for (let i = e.resultIndex; i < e.results.length; i += 1) {
        const resultat = e.results[i];
        if (resultat?.isFinal) texte += resultat[0].transcript;
      }
      if (texte.trim()) rappel.current(texte.trim());
    };
    r.onerror = (e) => {
      setErreur(
        e.error === 'not-allowed' || e.error === 'service-not-allowed'
          ? 'refusee'
          : e.error === 'no-speech' || e.error === 'aborted'
            ? null
            : 'indisponible',
      );
    };
    r.onend = () => {
      if (courante.current === r) courante.current = null;
      setEcoute(false);
    };
    courante.current = r;
    try {
      r.start();
      setEcoute(true);
    } catch {
      courante.current = null;
      setErreur('indisponible');
    }
  }, [autorisee, langue]);

  // Le réglage coupé ou l'écran fermé : le micro s'éteint (`onend` remet l'état).
  useEffect(() => {
    if (!autorisee) courante.current?.abort();
    return () => courante.current?.abort();
  }, [autorisee]);

  return { disponible, ecoute, erreur, demarrer, arreter };
}
